// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { Component } from "react";
import { connect } from "react-redux";
import { Routes, Navigate, Route } from "react-router-dom";

import PropTypes from "prop-types";
import { v4 as uuid } from "uuid";

import { Loader } from "components/elements";
import { PageError } from "components/layout";
import { withActiveOrganization } from "containers/Auth/decorators";
import { ExtendedRoute } from "containers/ExtendedRoute";
import { api } from "containers/Request";
import { APP_ROUTE_MY, SUBMISSION_STATUS } from "pages/Applications/constants";
import { API_URLS } from "src/config";
import { trans } from "src/translations";

import { getConfig } from "./config";
import { EditApp } from "./EditApp";
import { adaptToForm } from "./utils/appInventoryApiAdapter";
import { parseInitialData } from "./utils/parseInitialData";

@withActiveOrganization
@connect(null, (dispatch) => ({
  loadApplication: (id) => {
    return dispatch(
      api.getApplication.action({
        params: { id },
        // The 'nocache' query param forces backend to ignore a cache.
        // This is a temporary & hacky solution needed for the 1.2.0 release.
        // It has to be removed in the next release when caching in backend is fixed.
        queryParams: {
          nocache: uuid(),
          include: ["active_version", "last_submitted_version"],
        },
      })
    );
  },
  loadApplicationDraftData: (id) =>
    dispatch(api.getApplicationDraft.action({ params: { app_id: id } })),
  loadSnapRevisionData: (id, revisionId) =>
    dispatch(
      api.getSnapRevision.action({ params: { id, revision_id: revisionId } })
    ),
  loadTestApplicationData: (id) =>
    dispatch(api.getApplicationTestVersion.action({ params: { app_id: id } })),
}))
export class EditAppRoute extends Component {
  static propTypes = {
    params: PropTypes.object.isRequired,

    // from @connect
    loadApplication: PropTypes.func.isRequired,
    loadApplicationDraftData: PropTypes.func.isRequired,
    loadSnapRevisionData: PropTypes.func.isRequired,
    loadTestApplicationData: PropTypes.func.isRequired,

    // from @withActiveOrganization
    activeOrganization: PropTypes.shape({
      roles: PropTypes.array.isRequired,
    }).isRequired,
  };

  state = {
    isLoaded: false,
    application: null,
    draftVersion: null,
    fullVersion: null,
    testVersion: null,
    error: null,
    runServerValidation: false,
  };

  componentDidMount() {
    this.reloadState();
  }

  async componentDidUpdate(prevProps) {
    const activeOrganizationId = this.props.activeOrganization?.id;
    const prevActiveOrganizationId = prevProps.activeOrganization?.id;
    const orgChanged = activeOrganizationId !== prevActiveOrganizationId;

    const applicationIdChanged = this.props.params.id !== prevProps.params.id;

    if (orgChanged || applicationIdChanged) {
      this.reloadState();
    }
  }

  getSnapRevisionParams(notifyVersionStatusUrl) {
    if (!notifyVersionStatusUrl?.startsWith(API_URLS.SNAP_API)) {
      return null;
    }
    const match = notifyVersionStatusUrl
      .substring(API_URLS.SNAP_API.length)
      .match(/^\/apps\/([^/]+)\/revisions\/([^/]+)\//);
    if (!match) {
      return null;
    }
    return {
      id: match[1],
      revisionId: match[2],
    };
  }

  async makeRequests() {
    const {
      params: { id },
    } = this.props;
    this.setState({
      application: null,
      draftVersion: null,
      fullVersion: null,
      testVersion: null,
      isLoaded: false,
      error: null,
    });
    const applicationRequest = await this.props.loadApplication(id);
    if (applicationRequest.error) {
      throw applicationRequest.error;
    }

    this.setState({
      application: applicationRequest.result.results,
      fullVersion:
        applicationRequest.result.results?.active_version ||
        applicationRequest.result.results?.last_submitted_version,
    });

    const { is_test } = applicationRequest.result.results;
    if (is_test) {
      const testApplicationRequest =
        await this.props.loadTestApplicationData(id);
      this.setState({ testVersion: testApplicationRequest.result?.results });
    } else {
      const snapRevisionParams = this.getSnapRevisionParams(
        applicationRequest.result.results?.notify_version_status_url
      );
      const draftResults = await this.props.loadApplicationDraftData(id);
      let draftVersion = draftResults.result?.results;
      if (!draftVersion && snapRevisionParams) {
        const snapRevisionResults = await this.props.loadSnapRevisionData(
          snapRevisionParams.id,
          snapRevisionParams.revisionId
        );
        const snapRevision = snapRevisionResults.result;
        if (snapRevision) {
          draftVersion = {
            draft: {
              application_url: snapRevision.app_display_url,
              translations: snapRevision.translations.map((trans) => ({
                language: trans.language,
                is_default: trans.language === snapRevision.default_lang,
                app_title: trans.application_title,
                app_short_description:
                  this.state.fullVersion?.translations?.find(
                    ({ language }) => language === trans.language
                  )?.app_short_description || "",
              })),
            },
          };
        }
      }
      this.setState({ draftVersion });
    }
    this.setState({ isLoaded: true });
  }

  async reloadState({ runServerValidation } = { runServerValidation: false }) {
    try {
      this.setState({ runServerValidation });
      await this.makeRequests();
    } catch (error) {
      this.setState({ error, isLoaded: true });
    }
  }

  prepareApplicationProps() {
    const { application, draftVersion, fullVersion, testVersion } = this.state;
    const appData = { ...application };
    let initAppData = {};

    if (application.is_test) {
      const testApp = testVersion || {};
      initAppData = {
        ...initAppData,
        ...adaptToForm(testApp),
      };
    }

    if (application.has_submitted_version && !application.has_draft_version) {
      initAppData = { ...initAppData, ...adaptToForm(fullVersion) };
    }

    /*
     * Even when application.has_draft_version is false, draftVersion may still
     * be defined to fill in the application_url and translations fields.
     */
    if (draftVersion) {
      const adaptedDraft = adaptToForm(draftVersion.draft);
      if (application.has_draft_version) {
        initAppData = {
          ...initAppData,
          ...adaptedDraft,
        };
      } else {
        initAppData = {
          ...initAppData,
          metadata: {
            ...initAppData.metadata,
            application_url: adaptedDraft.metadata.application_url,
            translations: adaptedDraft.metadata.translations,
          },
        };
      }
    }

    return {
      appData,
      initAppData: parseInitialData(initAppData, application.has_draft_version),
    };
  }

  getApplicationError() {
    const { application } = this.state;

    let message = null;
    const appIsInReview =
      application?.submission_status === SUBMISSION_STATUS.IN_REVIEW.id;

    if (!application) {
      message = trans.APP_EDIT__ERROR_PAGE_DEFAULT_MESSAGE();
    } else if (appIsInReview) {
      message = trans.APP_EDIT__ERROR_PAGE_APP_IS_IN_REVIEW();
    }

    return message ? { message } : null;
  }

  getTabName = (config) => (match) => {
    // match that is injected from parent component does not yet have
    // the resolved value for ':tab' parameter. We have to use
    // better one from child ExtendedRoute component
    const activeTab =
      config.tabs.find((t) => t.url === match.tab) || config.tabs[0];
    return activeTab.name;
  };

  render() {
    const {
      params: { id },
      activeOrganization,
    } = this.props;
    const { isLoaded } = this.state;

    if (!isLoaded) {
      return <Loader />;
    }

    const error = this.state.error || this.getApplicationError();
    if (error) {
      return (
        <PageError
          pageHeader={trans.APP_EDIT__ERROR_PAGE_HEADER()}
          error={error}
          defaultMessage={trans.APP_EDIT__ERROR_PAGE_DEFAULT_MESSAGE()}
        />
      );
    }
    const applicationProps = this.prepareApplicationProps();
    const config = getConfig(applicationProps, activeOrganization);
    const defaultTab = config.tabs[0].url;
    const getTabName = this.getTabName(config);

    return (
      <Routes>
        <Route
          index
          element={
            <Navigate
              to={`/applications/${APP_ROUTE_MY}/${id}/edit/${defaultTab}`}
              replace={true}
            />
          }
        />
        <Route
          exact
          path="/edit"
          element={
            <Navigate
              to={`/applications/${APP_ROUTE_MY}/${id}/edit/${defaultTab}`}
              replace={true}
            />
          }
        />
        <Route
          exact
          path="/edit/*"
          element={
            <ExtendedRoute
              nameFromMatch={getTabName}
              render={(routeProps) => (
                <EditApp
                  {...applicationProps}
                  {...routeProps}
                  reloadState={this.reloadState.bind(this)}
                  config={config}
                  runServerValidation={this.state.runServerValidation}
                />
              )}
            />
          }
        />
      </Routes>
    );
  }
}
